import React from 'react';
import Calendar from 'react-calendar';
import { format, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import 'react-calendar/dist/Calendar.css';

const BackupCalendar = ({ backups, onDateSelect, selectedDate }) => {

  // Get all dates that have backups
  const backupDates = new Set();
  const backupsByDate = {};

  backups.forEach(backup => {
    const backupDate = new Date(backup.lastModified);
    const dateKey = format(backupDate, 'yyyy-MM-dd');
    backupDates.add(dateKey);
    
    if (!backupsByDate[dateKey]) {
      backupsByDate[dateKey] = [];
    }
    backupsByDate[dateKey].push(backup);
  });

  const tileClassName = ({ date, view }) => {
    if (view !== 'month') return null;
    
    const dateKey = format(date, 'yyyy-MM-dd');
    const hasBackups = backupDates.has(dateKey);
    const isSelected = selectedDate && isSameDay(date, selectedDate);
    
    let classes = [];
    
    if (hasBackups) {
      classes.push('has-backups');
    }
    
    if (isSelected) {
      classes.push('selected-date');
    }
    
    return classes.join(' ');
  };

  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    
    const dateKey = format(date, 'yyyy-MM-dd');
    const dayBackups = backupsByDate[dateKey];
    
    if (dayBackups && dayBackups.length > 0) {
      return (
        <div className="backup-count">
          {dayBackups.length}
        </div>
      );
    }
    
    return null;
  };

  const handleDateChange = (date) => {
    onDateSelect(date);
  };

  return (
    <div className="backup-calendar">
      <style jsx>{`
        .backup-calendar {
          background: white;
          border-radius: 0.5rem;
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        }
        
        .react-calendar {
          width: 100%;
          background: white;
          border: none;
          font-family: inherit;
          line-height: 1.125em;
        }
        
        .react-calendar__navigation {
          display: flex;
          height: 44px;
          margin-bottom: 1em;
        }
        
        .react-calendar__navigation button {
          min-width: 44px;
          background: none;
          font-size: 16px;
          margin-top: 8px;
          border: none;
          cursor: pointer;
          color: #374151;
        }
        
        .react-calendar__navigation button:enabled:hover,
        .react-calendar__navigation button:enabled:focus {
          background-color: #e5e7eb;
          border-radius: 0.375rem;
        }
        
        .react-calendar__tile {
          max-width: 100%;
          position: relative;
          padding: 0.75rem 0.5rem;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 0.875rem;
          line-height: 1.25rem;
          color: #374151;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 48px;
        }
        
        .react-calendar__tile:enabled:hover,
        .react-calendar__tile:enabled:focus {
          background-color: #f3f4f6;
          border-radius: 0.375rem;
        }
        
        .react-calendar__tile.has-backups {
          background-color: #dbeafe;
          color: #1e40af;
          font-weight: 500;
          border-radius: 0.375rem;
        }
        
        .react-calendar__tile.selected-date {
          background-color: #3b82f6 !important;
          color: white !important;
          border-radius: 0.375rem;
        }
        
        .react-calendar__tile--now {
          background-color: #fef3c7;
          border-radius: 0.375rem;
        }
        
        .react-calendar__tile--now.has-backups {
          background-color: #1e40af;
          color: white;
        }
        
        .backup-count {
          position: absolute;
          top: 2px;
          right: 2px;
          background-color: #dc2626;
          color: white;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 600;
        }
        
        .react-calendar__tile.selected-date .backup-count {
          background-color: #fbbf24;
          color: #92400e;
        }
        
        .react-calendar__month-view__weekdays {
          text-align: center;
          text-transform: uppercase;
          font-weight: bold;
          font-size: 0.75em;
          color: #6b7280;
        }
        
        .react-calendar__month-view__weekdays__weekday {
          padding: 0.5em;
        }
        
        .react-calendar__month-view__weekdays__weekday abbr {
          text-decoration: none;
        }
      `}</style>
      
      <Calendar
        onChange={handleDateChange}
        value={selectedDate}
        tileClassName={tileClassName}
        tileContent={tileContent}
        locale="pt-BR"
        formatShortWeekday={(locale, date) => 
          format(date, 'eee', { locale: ptBR }).charAt(0).toUpperCase()
        }
        navigationLabel={({ date }) => 
          format(date, 'MMMM yyyy', { locale: ptBR })
        }
      />
    </div>
  );
};

export default BackupCalendar;